import { jobStatusConfig } from '@/pages/ats-job-creation/utils/constants';
import { Flex, Icon, Button, Box } from '@chakra-ui/react';
import { Copy03 } from '@kula-ai/icons/dual-tone/Copy03';
import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CopyJobHeader } from './CopyJobHeader';
import { CopyTemplateHeader } from './CopyTemplateHeader';
import JobDescriptionCopy from './JobDescriptionCopy';
import { JobStages } from './JobStages';
import { getAtsJobs } from '@/store/atsJobs';
import { atsJobsActions } from '@/store/atsJobs';
import { useDispatch, useSelector } from 'react-redux';
import { InterviewPlan } from './InterviewPlan';
import { ViewMorePopover } from '../ViewMorePopover';
import { fetchJobsWithFilter } from '@/pages/ats-jobs/utils/jobsHelpers';
import { CenterSpinner } from '../loadders';
import { KText } from '@/ui-library/components/text';
import { KTag } from '@/ui-library/components';
import { KDrawer } from '@/ui-library/components/molecules/KDrawer';

const DefaultRightComp = ({ job = {} }) => {
  const { name = '' } = job;
  return (
    <Flex w="100%" border="1px solid #EAEAEA" p="1em">
      <KText>{name}</KText>
    </Flex>
  );
};

const typeConfig = {
  jd: JobDescriptionCopy,
  jobStage: JobStages,
  interviewPlan: InterviewPlan,
  interviewPlanTemplate: InterviewPlan,
  default: DefaultRightComp,
};

export const CopyFromModal = ({
  label = 'copy from another job',
  icon = Copy03,
  modalConfig = {},
  showCustomButtonComp = false,
  apiCall,
  ButtonComp = <></>,
  type = 'default',
  isCopyFromTemp = false,
  isDisabled = false,
  showCta = true,
  row = {},
  apiType,
  selected,
  unSelected,
  jobStageId,
  toggleAllRowsSelected,
  resetSelectionValue,
  modalActions = {},
  iconProps = {},
  isCtaLoading = false,
  currentStage,
  atsStages,
  renderValidationBanner,
}) => {
  const [selectedJob, setSelectedJob] = useState({});
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [selectedStage, setSelectedStage] = useState(undefined);
  const [localFilters, setLocalFilters] = useState({});
  const { isOpen, onClose, onOpen } = modalActions;
  const dispatch = useDispatch();
  const { jobId } = useParams();
  const { applySearch } = atsJobsActions || {};

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedJob({});
      setSelectedStage(undefined);
      setLocalFilters({}); // Reset local filters
    }
  }, [isOpen]);

  // Clear search term from Redux on unmount
  useEffect(() => {
    return () => {
      dispatch(applySearch?.({ searchTerm: '' }));
    };
  }, [dispatch, applySearch]);

  const {
    currentView = {},
    filters,
    sort,
    page,
    searchTerm,
    getAllJobs: { data = {}, isLoading } = {},
  } = useSelector(getAtsJobs);

  const { ats_jobs = [] } = data;

  // Filter jobs based on type
  const filteredJobs = useMemo(() => {
    const jobsWithoutCurrent = ats_jobs.filter(
      (job) => Number(job.id) !== Number(jobId)
    );

    // Local search for interview plan templates
    if (type === 'interviewPlanTemplate' && localFilters?.name) {
      return jobsWithoutCurrent.filter(
        (job) =>
          job.name.toLowerCase().includes(localFilters.name.toLowerCase()) ||
          job.stages?.some((stage) =>
            stage.name.toLowerCase().includes(localFilters.name.toLowerCase())
          )
      );
    }

    return jobsWithoutCurrent;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ats_jobs, jobId, type, localFilters, filters, searchTerm]);

  const isJobSelectable = (job) => {
    if (type === 'interviewPlan' || type === 'interviewPlanTemplate') {
      return (job?.stages?.length || 0) > 0;
    }
    return true;
  };

  const {
    modalActionText = 'Import this description',
    modalAction = () => {},
  } = modalConfig;

  const handleModalSubmit = () => {
    if (type === 'jd')
      return modalAction(selectedJob?.job_description, onClose);
    const payload = {
      selectedJob,
      selectedStage,
      selected: apiType === 'bulk' ? selected : row?.original,
      unSelected,
      jobStageId,
      type: apiType,
      currentStage,
      atsStages,
      setIsLoading: setIsActionLoading,
      handleOnSuccessClose: () => {
        onClose();
        toggleAllRowsSelected(false);
        resetSelectionValue();
      },
    };
    return modalAction(payload);
  };

  const RightComp = typeConfig[type];

  //commenting this, as we are doing filter based api call
  // useEffect(() => {
  // if (isOpen && apiCall) apiCall();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let updatedCurrentView =
      type === 'jd'
        ? 'job_description'
        : type === 'interviewPlanTemplate'
        ? 'template_interview_plan'
        : type === 'interviewPlan'
        ? 'interview_plan'
        : currentView;
    const updatedFilters = type === 'interviewPlanTemplate' ? {} : filters;
    const updatedSearchTerm =
      type === 'interviewPlanTemplate' ? undefined : searchTerm;
    fetchJobsWithFilter({
      currentView: updatedCurrentView,
      filters: updatedFilters,
      sort,
      page: {
        limit: 100,
      },
      searchTerm: updatedSearchTerm,
      dispatch,
      type,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, type, filters, sort, page, searchTerm]);

  // Effect to preselect first job when data is loaded
  useEffect(() => {
    if (!isOpen || !filteredJobs.length) return;

    // For interview plan types, we already filtered out jobs without stages
    setSelectedJob(filteredJobs[0]);
  }, [filteredJobs, isOpen]);

  const renderLeftContent = (job = {}, index) => {
    const currentJobID = window.location.pathname.split('/')[2];
    if (Number(currentJobID) === Number(job?.id)) {
      return null;
    }
    const {
      name,
      state,
      department,
      offices = [],
      id,
      kind,
      stage_count: stageCount,
    } = job;
    const { color, label } = jobStatusConfig[state] || {};

    const hasStages = isJobSelectable(job);
    const isSelected = id === selectedJob?.id;

    const handleItemClick = () => {
      setSelectedJob(job);
    };

    return (
      <Flex
        key={index}
        w="100%"
        cursor="pointer"
        onClick={handleItemClick}
        flexDir="column"
        borderBottom="1px solid"
        borderColor="border-color"
        p="1em"
        gap=".5em"
        bgColor={isSelected ? 'secondary-50' : undefined}
        _hover={{
          bgColor: 'secondary-50',
        }}
      >
        <Flex justifyContent="space-between" w="100%">
          <KText size="md-semi-bold">{name}</KText>
          {isCopyFromTemp ? (
            <Flex
              alignItems="center"
              gap=".3em"
              p=".25em .5em"
              border="1px solid"
              borderColor="border-color"
              borderRadius="xs"
              bgColor={id === selectedJob?.id ? 'white' : 'secondary-50'}
            >
              <KText
                size="sm-medium"
                color={hasStages ? 'secondary-700' : 'red.500'}
              >
                {hasStages ? `${stageCount} stages` : 'No stages'}
              </KText>
            </Flex>
          ) : (
            <Flex gap="10px">
              {kind === 'sample' && (
                <Flex
                  p="0.125em 0.5em"
                  alignItems="center"
                  borderRadius="6px"
                  bg="#FFFAEB"
                  color="#B54708"
                >
                  <KText size="sm-medium" color="inherit">
                    Sample
                  </KText>
                </Flex>
              )}
              <Flex
                alignItems="center"
                gap=".3em"
                bgColor="white"
                p=".25em .5em"
                border="1px solid"
                borderColor="border-color"
                borderRadius="xs"
              >
                <Box
                  w="8px"
                  h="8px"
                  bgColor={color}
                  borderRadius="full"
                  flexShrink={0}
                ></Box>
                <KText size="sm-medium" lineHeight={'1.1em'}>
                  {label}
                </KText>
              </Flex>
            </Flex>
          )}
        </Flex>
        {!isCopyFromTemp && (
          <Flex gap=".5em">
            {department && (
              <KTag
                border="1px solid"
                borderColor="border-color"
                borderRadius="4px"
                bgColor={id === selectedJob?.id ? 'base-white' : 'secondary-50'}
                value={department}
              />
            )}
            {offices?.length !== 0 && (
              <KTag
                border="1px solid"
                borderColor="border-color"
                borderRadius="4px"
                bgColor={id === selectedJob?.id ? 'base-white' : 'secondary-50'}
                value={
                  <ViewMorePopover
                    list={offices}
                    emptyMsg=""
                    popOverStyles={{
                      maxW: '15em',
                    }}
                    moreTextStyles={{
                      size: 'sm',
                      color: 'secondary-700',
                      _hover: { color: 'primary-500' },
                    }}
                  />
                }
              />
            )}
          </Flex>
        )}
      </Flex>
    );
  };

  const renderBody = () => {
    const isInterviewPlanType =
      type === 'interviewPlan' || type === 'interviewPlanTemplate';

    // Helper to get appropriate message when no results are found
    const getNoResultsMessage = () => {
      const itemType = isCopyFromTemp ? 'template' : 'job';
      const hasSearchTerm =
        type === 'interviewPlanTemplate' ? localFilters?.name : searchTerm;
      const hasFilters =
        type !== 'interviewPlanTemplate' &&
        Object.keys(filters || {}).length > 0;

      if (type === 'interviewPlanTemplate') {
        return hasSearchTerm
          ? `No templates found matching "${hasSearchTerm}"`
          : 'No templates found';
      }

      if (hasSearchTerm && hasFilters) {
        return `No ${itemType}s found matching your search and filters`;
      } else if (hasSearchTerm) {
        return `No ${itemType}s found matching "${hasSearchTerm}"`;
      } else if (hasFilters) {
        return `No ${itemType}s found matching your filters`;
      }
      return `No ${itemType}s found`;
    };

    return (
      <Flex
        w="100%"
        borderTop="1px solid"
        borderColor="border-color"
        h="calc(100vh - 200px)" // Set fixed height for the container
        overflow="hidden" // Prevent parent scroll
      >
        {ats_jobs?.length === 0 ? (
          <Flex width="100%" alignItems="center" justifyContent="center">
            <KText size="lg-semi-bold">{getNoResultsMessage()}</KText>
          </Flex>
        ) : filteredJobs.length === 0 && isInterviewPlanType ? (
          <Flex width="100%" alignItems="center" justifyContent="center">
            <KText size="lg-semi-bold">{getNoResultsMessage()}</KText>
          </Flex>
        ) : (
          <>
            <Flex
              w="40%"
              flexDir="column"
              borderRight="1px solid"
              borderColor="border-color"
              h="100%"
              overflowY="auto"
            >
              {filteredJobs?.map(renderLeftContent)}
            </Flex>
            <Flex w="60%" p="xl" h="100%" overflowY="auto" flexDir="column">
              <Box flex="1">
                <RightComp
                  job={selectedJob}
                  selectedJob={selectedJob}
                  selectedStage={selectedStage}
                  setSelectedStage={setSelectedStage}
                />
              </Box>
              {renderValidationBanner &&
                type === 'jobStage' &&
                renderValidationBanner({
                  selectedJob,
                  selectedStage,
                })}
            </Flex>
          </>
        )}
      </Flex>
    );
  };

  const isButtonDisabled = useMemo(() => {
    if (!selectedJob?.id || isCtaLoading) return true;

    if (type === 'jd' && !selectedJob?.job_description) return true;

    if (
      ['interviewPlan', 'interviewPlanTemplate'].includes(type) &&
      !isJobSelectable(selectedJob)
    )
      return true;

    if (type === 'jobStage') {
      return !selectedJob?.stages?.length || !selectedStage;
    }

    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob, selectedStage, type, isCtaLoading]);

  const renderModal = () => {
    return (
      <KDrawer
        isOpen={isOpen}
        showCloseButton={true}
        onClose={onClose}
        handleOutsideClose={() => {}}
        contentProps={{
          maxW: '75%',
          borderTopLeftRadius: 'xl',
        }}
        header={
          isCopyFromTemp ? (
            <CopyTemplateHeader
              filters={
                type === 'interviewPlanTemplate' ? localFilters : filters
              }
              setFilters={
                type === 'interviewPlanTemplate' ? setLocalFilters : undefined
              }
              type={type}
              modalConfig={modalConfig}
            />
          ) : (
            <CopyJobHeader
              filters={filters}
              type={type}
              modalConfig={modalConfig}
            />
          )
        }
        children={isLoading ? <CenterSpinner /> : renderBody()}
        footer={
          <Flex
            borderTop="1px solid"
            borderColor="border-color"
            p="1em"
            flexDir={'row-reverse'}
            gap="6px"
          >
            <Button
              disabled={isButtonDisabled || isActionLoading}
              onClick={handleModalSubmit}
              variant="primary"
              isLoading={isCtaLoading || isActionLoading}
            >
              {modalActionText}
            </Button>
            <Button variant="secondary" mr={3} onClick={onClose}>
              Cancel
            </Button>
          </Flex>
        }
      ></KDrawer>
    );
  };

  const renderCTA = () => {
    return (
      <>
        {showCustomButtonComp ? (
          <ButtonComp
            icon={icon}
            text={label}
            onClick={onOpen}
            iconProps={iconProps}
          />
        ) : (
          <Flex
            gap=".5em"
            alignItems="center"
            onClick={!isDisabled && onOpen}
            opacity={isDisabled ? 0.5 : 1}
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
          >
            <Icon as={icon} color="primary-500" {...iconProps} />
            <KText size="sm-medium" color="primary-500">
              {label}
            </KText>
          </Flex>
        )}
      </>
    );
  };

  return (
    <>
      {showCta && renderCTA()}
      {isOpen && renderModal()}
    </>
  );
};
